const EMPTY = '—';

const COMPACT_UNITS: [number, string][] = [
  [1_000_000_000, 'B'],
  [1_000_000, 'M'],
  [1_000, 'K'],
];

/** Number formatting for report tiles, tables, and chart axes. */
export class ReportFormatHelper {
  static currency(
    value: number | null | undefined,
    currency: string,
    opts: { compact?: boolean; decimals?: number } = {}
  ): string {
    if (value === null || value === undefined || Number.isNaN(value))
      return EMPTY;
    const { compact = false, decimals = 0 } = opts;
    const unit = compact
      ? COMPACT_UNITS.find(([div]) => Math.abs(value) >= div)
      : undefined;
    if (unit) {
      const [div, suffix] = unit;
      const scaled = value / div;
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: Math.abs(scaled) >= 100 ? 0 : 1,
      }).format(scaled);
      return `${formatted}${suffix}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  static number(value: number | null | undefined, decimals = 0): string {
    if (value === null || value === undefined || Number.isNaN(value))
      return EMPTY;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  /** `value` is a ratio (0.25 -> "25.0%"). */
  static percent(
    value: number | null | undefined,
    decimals = 1,
    signed = false
  ): string {
    if (value === null || value === undefined || !Number.isFinite(value))
      return EMPTY;
    const formatted = `${(value * 100).toFixed(decimals)}%`;
    return signed && value > 0 ? `+${formatted}` : formatted;
  }

  /** Signed compact currency, e.g. "+$12.3K" / "−$4K". */
  static delta(value: number | null | undefined, currency: string): string {
    if (value === null || value === undefined) return EMPTY;
    const formatted = ReportFormatHelper.currency(Math.abs(value), currency, {
      compact: true,
    });
    if (value > 0) return `+${formatted}`;
    if (value < 0) return `−${formatted}`;
    return formatted;
  }

  static months(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value))
      return EMPTY;
    if (value > 120) return '120+ mo';
    return `${value.toFixed(1)} mo`;
  }

  static days(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value))
      return EMPTY;
    return `${Math.round(value)} d`;
  }
}
